import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { LinearProgress } from '@material-ui/core';
import { useForm, FormProvider } from 'react-hook-form';
import moment, { Moment } from 'moment';

import { useInjectReducer } from 'utils/injectReducer';
import { useInjectEpics } from 'utils/injectEpics';
import OrganizationApi from 'entities/organization/api';
import { selectUser } from 'entities/user/selectors';
import { selectSDGs } from 'entities/cause/selectors';
import { Privileges } from 'utils/privileges';
import { Features } from 'utils/features';
import { Action } from 'shared/types';
import Box from 'retired/Box';
import {
  useActionsWithPaginationQuery,
  ActionVisibilityStatus,
  MutationMutationUpsertActionArgs,
  IncentiveInputType,
  IncentiveType,
} from 'generated/graphql';
import ActionForm from 'components/Actions/ActionForm';
import {
  FormFieldValues,
  DEFAULT_REWARD_UNIT,
  DEFAULT_USD_REWARD_AMOUNT,
  DEFAULT_TREE_REWARD_ORGANIZATION,
} from 'components/Actions/types';

import epics from './epics';
import reducer from './reducer';
import { selectLoading } from './selectors';
import { initAction } from './types';
import { useActionMutation } from './useActionMutation';
const key = 'actionPage';

const getDefaultValues = (action?: Action, duplicate?: boolean): FormFieldValues => {
  if (!action) {
    return {
      title: '',
      subTitle: '',
      description: '',
      bannerImage: '',
      campaignIds: [],
      rewardAmount: DEFAULT_USD_REWARD_AMOUNT,
      rewardUnit: DEFAULT_REWARD_UNIT,
      impactUnit: '',
      impactAmount: 0,
      causes: [],
      categories: [],
      sdgs: [],
      nonprofitId: null,
      nonprofit: null,
      startingAt: null,
      endingAt: null,
      status: 'draft',
      enabled: true,
    };
  }

  const categories = action.Causes.filter((cause) => cause.type === 'Category').map((c) => c.id);
  const sdgs = action.Causes.filter((cause) => cause.type === 'SDG').map((c) => c.id);

  const reward = action.incentives.find((i) => i.type === IncentiveType.Reward);
  const { unit: rewardUnit, amount: rewardAmount } = reward ?? {
    unit: DEFAULT_REWARD_UNIT,
    // since action is published and there is no reward amount, implies that actionReward is not required
    amount: action.status === 'published' ? 0 : DEFAULT_USD_REWARD_AMOUNT,
  };

  const impact = action.incentives.find((i) => i.type === IncentiveType.Impact);

  return {
    title: duplicate ? `Duplicate of ${action.title}` : action.title,
    subTitle: action.subTitle || '',
    description: action.description || '',
    bannerImage: action.bannerImage || '',
    campaignIds: action.campaignIds || [],
    rewardUnit,
    rewardAmount,
    impactUnit: impact?.unit || '',
    impactAmount: impact?.amount || 0,
    causes: action.Causes,
    categories,
    sdgs,
    nonprofitId: action.nonprofitId || null,
    nonprofit: action.Nonprofit ? { id: action.Nonprofit.id, name: action.Nonprofit.name } : null,
    startingAt: action.startingAt,
    endingAt: action.endingAt,
    status: action.status,
    enabled: action.enabled,
  };
};

const getDateString = (date: Date | Moment | string) =>
  typeof date !== 'string' && 'toISOString' in date ? date.toISOString() : date;

const setStartOfDay = (date: Date | Moment | string) => moment(date).clone().startOf('day');
const setEndOfDay = (date: Date | Moment | string) => moment(date).clone().endOf('day');

const buildActionAndTemplatePayload = ({
  actionId,
  actionTemplateId,
  formValues,
  treeNonprofit,
  duplicate,
}: {
  actionId?: string;
  actionTemplateId?: string;
  formValues: FormFieldValues;
  treeNonprofit?: { id: string; name: string };
  duplicate?: boolean;
}): MutationMutationUpsertActionArgs => {
  const causesIds = [...(formValues.categories || []), ...(formValues.sdgs || [])];
  const incentives: IncentiveInputType[] = [];

  if (formValues.rewardAmount && formValues.rewardUnit) {
    incentives.push({
      type: IncentiveType.Reward,
      unit: formValues.rewardUnit,
      amount: Number(formValues.rewardAmount),
    });
  }
  if (formValues.impactAmount && formValues.impactUnit) {
    incentives.push({
      type: IncentiveType.Impact,
      unit: formValues.impactUnit,
      amount: Number(formValues.impactAmount),
    });
  }

  const selectedNonprofit =
    formValues.rewardUnit === 'tree' && treeNonprofit ? treeNonprofit : formValues.nonprofit;
  const nonprofitId = selectedNonprofit?.id;

  return {
    actionData: {
      ...(duplicate ? {} : { id: actionId }),
      actionTemplateId,
      title: formValues.title,
      subTitle: formValues.subTitle,
      description: formValues.description,
      bannerImage: formValues.bannerImage,
      startingAt: formValues.startingAt
        ? getDateString(setStartOfDay(formValues.startingAt))
        : null,
      endingAt: formValues.endingAt ? getDateString(setEndOfDay(formValues.endingAt)) : null,
      campaignIds: formValues.campaignIds,
      incentives,
      causes: causesIds,
      nonprofitId,
      status: formValues.status as ActionVisibilityStatus,
      enabled: formValues.enabled,
    },
  };
};

const ActionPage: React.FC<{}> = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const actionId = id !== 'new' ? id : undefined;

  const { search } = useLocation();
  const queryParams = new URLSearchParams(search);
  const duplicate = Boolean(queryParams.get('duplicate'));

  useInjectReducer({ key, reducer });
  useInjectEpics({ key, epics });

  const user = useSelector(selectUser);
  const SDGs = useSelector(selectSDGs);

  const isActionsEnabled =
    user && user.hasFeature(Features.ACTIONS) && user.hasPrivilege(Privileges.ACTIONS_MANAGE);

  const [treeNonprofit, setTreeNonprofit] = useState<{ id: string; name: string } | undefined>();

  const actionQuery = useActionsWithPaginationQuery({
    variables: {
      where: { id: { equals: actionId } },
      take: 1,
      skip: 0,
    },
    skip: !isActionsEnabled || !actionId,
  });

  const [actionMutation, { loading: loadingActionMutation }] = useActionMutation(actionId);

  const action = actionQuery.data?.actionsWithPagination?.items?.[0];
  const actionTemplateId = action?.actionTemplateId;

  const formMethods = useForm<FormFieldValues>({
    mode: 'all',
    defaultValues: getDefaultValues(action, duplicate),
  });

  const handleSubmit = formMethods.handleSubmit((formValues: FormFieldValues) => {
    const data = buildActionAndTemplatePayload({
      actionId,
      actionTemplateId,
      formValues,
      treeNonprofit,
      duplicate,
    });

    const isPublishing = formMethods?.getValues()?.status === 'published';
    if (!formMethods?.formState?.isValid && isPublishing) {
      alert('Some required fields are missing. You can save it as a draft for now.');
    } else {
      actionMutation(data);
    }
  });

  const loading = useSelector(selectLoading) || actionQuery.loading;
  const actionHasActivities = Boolean(actionId && action?.Activities.length);

  useEffect(() => {
    dispatch(initAction(actionId || 'new'));
  }, [dispatch, actionId]);

  useEffect(() => {
    formMethods.reset(getDefaultValues(action, duplicate));
  }, [action, formMethods]);

  useEffect(() => {
    if (isActionsEnabled) {
      const searchNonprofitDB = user?.organization?.settings?.get('searchNonprofitDB') || false;

      OrganizationApi.searchNonprofits(
        {
          searchTerm: DEFAULT_TREE_REWARD_ORGANIZATION,
          countryCode: 'US',
          acceptsDonations: true,
        },
        searchNonprofitDB
      ).subscribe((nonprofits) => {
        const nonprofit = nonprofits?.find((n) => n.name === DEFAULT_TREE_REWARD_ORGANIZATION);
        if (nonprofit) {
          setTreeNonprofit({ id: nonprofit.id, name: nonprofit.name });
        }
      });
    }
  }, []);

  useEffect(() => {
    if (action) {
      formMethods?.trigger();
    }
  }, [action]);

  if (!isActionsEnabled) {
    history.replace('/');
  }

  if (actionId && !loading && !action) {
    history.replace('/notfound');
  }

  return (
    <>
      {loading ? (
        <Box>
          <LinearProgress />
        </Box>
      ) : (
        <FormProvider {...formMethods}>
          <ActionForm
            title={duplicate ? `Duplicate Action` : 'Action'}
            formMethods={formMethods}
            handleSubmit={handleSubmit}
            isLoading={loadingActionMutation}
            actionHasActivities={actionHasActivities}
            treeNonprofit={treeNonprofit}
            showSDGs={SDGs?.size > 0}
            showDates
            showSelectNonprofit
            showCampaignTip
            showSelectVisibility={false}
            backLink="/deeds/actions"
          />
        </FormProvider>
      )}
    </>
  );
};

export default ActionPage;
